/**
 * Video transition catalog for the timeline editor and export.
 * `xfade` maps to FFmpeg xfade filter names for local (wasm) export when supported.
 */

export const TRANSITION_CATEGORIES = [
  { id: 'basic', label: 'Basic & Professional' },
  { id: 'dynamic', label: 'Dynamic & Modern' },
  { id: 'cinematic', label: 'Cinematic' },
  { id: 'object', label: 'Object-Based' },
  { id: 'ai_doc', label: 'AI / Documentary' },
  { id: 'shorts', label: 'Shorts & Reels' },
];

/** @type {Array<{ id: string, label: string, category: string, useCase?: string, defaultDuration: number, xfade?: string | null }>} */
export const VIDEO_TRANSITIONS = [
  // Basic & Professional
  { id: 'cut', label: 'Cut', category: 'basic', useCase: 'Instant change; fast-paced content', defaultDuration: 0, xfade: null },
  { id: 'fade_in', label: 'Fade In', category: 'basic', useCase: 'Opening scenes', defaultDuration: 0.6, xfade: 'fade' },
  { id: 'fade_out', label: 'Fade Out', category: 'basic', useCase: 'Ending scenes, emotional moments', defaultDuration: 0.6, xfade: 'fade' },
  { id: 'cross_dissolve', label: 'Cross Dissolve', category: 'basic', useCase: 'Smooth transition between related scenes', defaultDuration: 0.5, xfade: 'dissolve' },
  { id: 'dip_to_black', label: 'Dip to Black', category: 'basic', useCase: 'Passage of time, scene ending', defaultDuration: 0.6, xfade: 'fadeblack' },
  { id: 'dip_to_white', label: 'Dip to White', category: 'basic', useCase: 'Flashbacks, dream sequences', defaultDuration: 0.5, xfade: 'fadewhite' },

  // Dynamic & Modern
  { id: 'whip_pan', label: 'Whip Pan', category: 'dynamic', useCase: 'Travel vlogs, action sequences', defaultDuration: 0.4, xfade: 'smoothleft' },
  { id: 'zoom_in', label: 'Zoom In', category: 'dynamic', useCase: 'Reels, Shorts, TikTok', defaultDuration: 0.45, xfade: 'zoomin' },
  { id: 'zoom_out', label: 'Zoom Out', category: 'dynamic', useCase: 'Reveal next scene', defaultDuration: 0.45, xfade: 'zoomin' },
  { id: 'motion_blur', label: 'Motion Blur', category: 'dynamic', useCase: 'Seamless movement between clips', defaultDuration: 0.4, xfade: 'hblur' },
  { id: 'spin', label: 'Spin Transition', category: 'dynamic', useCase: 'Energetic content, sports edits', defaultDuration: 0.5, xfade: 'radial' },
  { id: 'push_left', label: 'Push Left', category: 'dynamic', useCase: 'Location or topic change', defaultDuration: 0.45, xfade: 'coverleft' },
  { id: 'push_right', label: 'Push Right', category: 'dynamic', useCase: 'Location or topic change', defaultDuration: 0.45, xfade: 'coverright' },
  { id: 'slide_left', label: 'Slide Left', category: 'dynamic', useCase: 'Moving to next topic', defaultDuration: 0.45, xfade: 'slideleft' },
  { id: 'slide_right', label: 'Slide Right', category: 'dynamic', useCase: 'Moving to next topic', defaultDuration: 0.45, xfade: 'slideright' },

  // Cinematic
  { id: 'film_burn', label: 'Film Burn', category: 'cinematic', useCase: 'Vintage and documentary videos', defaultDuration: 0.6, xfade: 'fadewhite' },
  { id: 'light_leak', label: 'Light Leak', category: 'cinematic', useCase: 'Emotional cinematic storytelling', defaultDuration: 0.55, xfade: 'fadewhite' },
  { id: 'lens_flare', label: 'Lens Flare', category: 'cinematic', useCase: 'Travel and lifestyle content', defaultDuration: 0.5, xfade: 'fadewhite' },
  { id: 'match_cut', label: 'Match Cut', category: 'cinematic', useCase: 'Matching shapes or actions', defaultDuration: 0.35, xfade: 'dissolve' },
  { id: 'morph_cut', label: 'Morph Cut', category: 'cinematic', useCase: 'Interviews and talking-head videos', defaultDuration: 0.4, xfade: 'dissolve' },

  // Object-Based (approximated with motion wipes)
  { id: 'person_walk_reveal', label: 'Person Walk Reveal', category: 'object', useCase: 'Subject crosses frame to reveal next scene', defaultDuration: 0.5, xfade: 'coverleft' },
  { id: 'lens_cover_reveal', label: 'Lens Cover Reveal', category: 'object', useCase: 'Object covers lens then reveals', defaultDuration: 0.5, xfade: 'circleclose' },
  { id: 'doorway_travel', label: 'Doorway / Wall Travel', category: 'object', useCase: 'Camera enters object, exits elsewhere', defaultDuration: 0.55, xfade: 'zoomin' },
  { id: 'hand_swipe', label: 'Hand Swipe', category: 'object', useCase: 'Hand swipe across screen', defaultDuration: 0.4, xfade: 'wipeleft' },
  { id: 'drone_rise', label: 'Drone Rise', category: 'object', useCase: 'Rising aerial into next scene', defaultDuration: 0.55, xfade: 'slideup' },

  // AI / Documentary
  { id: 'slow_cross_dissolve', label: 'Slow Cross Dissolve', category: 'ai_doc', useCase: 'Documentary pacing', defaultDuration: 1.0, xfade: 'fadeslow' },
  { id: 'parallax_fade', label: 'Parallax + Fade', category: 'ai_doc', useCase: 'Layered documentary motion', defaultDuration: 0.7, xfade: 'dissolve' },
  { id: 'smoke', label: 'Smoke Transition', category: 'ai_doc', useCase: 'Atmospheric scene change', defaultDuration: 0.6, xfade: 'dissolve' },
  { id: 'ink_spread', label: 'Ink Spread', category: 'ai_doc', useCase: 'Illustration documentaries', defaultDuration: 0.55, xfade: 'circleopen' },
  { id: 'ancient_map_reveal', label: 'Ancient Map Reveal', category: 'ai_doc', useCase: 'History / geography reveals', defaultDuration: 0.65, xfade: 'revealup' },
  { id: 'page_turn', label: 'Page Turn', category: 'ai_doc', useCase: 'Book or manuscript storytelling', defaultDuration: 0.5, xfade: 'wiperight' },
  { id: 'particle_dissolve', label: 'Particle Dissolve', category: 'ai_doc', useCase: 'Soft magical dissolve', defaultDuration: 0.6, xfade: 'pixelize' },
  { id: 'light_sweep', label: 'Light Sweep', category: 'ai_doc', useCase: 'Highlight sweep across frame', defaultDuration: 0.45, xfade: 'hrwind' },
  { id: 'illustration_match_cut', label: 'Match Cut (Illustrations)', category: 'ai_doc', useCase: 'Illustration shape matching', defaultDuration: 0.35, xfade: 'dissolve' },

  // Shorts & Reels
  { id: 'zoom_blur', label: 'Zoom Blur', category: 'shorts', useCase: 'High-energy short-form', defaultDuration: 0.35, xfade: 'hblur' },
  { id: 'flash', label: 'Flash Transition', category: 'shorts', useCase: 'Quick bright hit', defaultDuration: 0.25, xfade: 'fadefast' },
  { id: 'speed_ramp', label: 'Speed Ramp', category: 'shorts', useCase: 'Momentum into next clip', defaultDuration: 0.3, xfade: 'fade' },
  { id: 'camera_shake', label: 'Camera Shake', category: 'shorts', useCase: 'Impact or chaos cut', defaultDuration: 0.3, xfade: 'hblur' },
  { id: 'glitch', label: 'Glitch Transition', category: 'shorts', useCase: 'Digital / edgy edits', defaultDuration: 0.35, xfade: 'pixelize' },
  { id: 'swipe_left', label: 'Swipe Left', category: 'shorts', useCase: 'Mobile-style swipe', defaultDuration: 0.35, xfade: 'wipeleft' },
  { id: 'swipe_right', label: 'Swipe Right', category: 'shorts', useCase: 'Mobile-style swipe', defaultDuration: 0.35, xfade: 'wiperight' },
  { id: 'rgb_split', label: 'RGB Split', category: 'shorts', useCase: 'Chromatic aberration effect', defaultDuration: 0.35, xfade: 'pixelize' },
  { id: 'spin_zoom', label: 'Spin Zoom', category: 'shorts', useCase: 'Spinning zoom punch', defaultDuration: 0.4, xfade: 'zoomin' },
  { id: 'stretch', label: 'Stretch Transition', category: 'shorts', useCase: 'Elastic stretch between clips', defaultDuration: 0.35, xfade: 'squeezeh' },
  { id: 'mask_reveal', label: 'Mask Reveal', category: 'shorts', useCase: 'Masked shape reveal', defaultDuration: 0.45, xfade: 'circleopen' },
];

export const TRANSITION_IDS = new Set(VIDEO_TRANSITIONS.map((t) => t.id));

export const DEFAULT_TRANSITION_OUT = 'cut';
export const DEFAULT_TRANSITION_DURATION = 0.5;

export function getTransitionById(id) {
  return VIDEO_TRANSITIONS.find((t) => t.id === id) || VIDEO_TRANSITIONS[0];
}

export function normalizeTransitionOut(id) {
  const key = String(id || '').trim().toLowerCase().replace(/-/g, '_');
  if (!key || key === 'none') return DEFAULT_TRANSITION_OUT;
  return TRANSITION_IDS.has(key) ? key : DEFAULT_TRANSITION_OUT;
}

export function defaultDurationForTransition(id) {
  const t = getTransitionById(normalizeTransitionOut(id));
  return t?.defaultDuration ?? DEFAULT_TRANSITION_DURATION;
}

export function transitionsByCategory() {
  const map = Object.fromEntries(TRANSITION_CATEGORIES.map((c) => [c.id, []]));
  for (const t of VIDEO_TRANSITIONS) {
    if (map[t.category]) map[t.category].push(t);
  }
  return map;
}

export function transitionShortLabel(id) {
  const t = getTransitionById(normalizeTransitionOut(id));
  if (!t || t.id === 'cut') return '';
  return t.label.split(' ')[0];
}
